import React, { useState, useEffect } from "react";
import axios from "axios";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Plus, Edit2, Trash2, Image as ImageIcon, X, AlertTriangle, Eye } from "lucide-react";

const ManageBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("Admin");
  const [tags, setTags] = useState("");
  const [layoutStyle, setLayoutStyle] = useState("carousel");
  const [content, setContent] = useState("");
  const [newImages, setNewImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editId, setEditId] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "clean"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "link",
  ];

  const fetchBlogs = async () => {
    try {
      const res = await axios.get(window.API_URL + "/blogs");
      setBlogs(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Check if adding these files would exceed 4 total images (existing + new selected)
    const totalCount = existingImages.length + newImages.length + files.length;
    if (totalCount > 4) {
      setErrorMsg("You can upload a maximum of 4 images per blog post.");
      return;
    }
    
    setErrorMsg("");
    setNewImages((prev) => [...prev, ...files]);
  };

  const removeNewImage = (index) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    if (!content || content.trim() === "<p><br></p>" || content.trim() === "") {
      setErrorMsg("Content cannot be empty!");
      return;
    }

    const totalImages = existingImages.length + newImages.length;
    if (totalImages === 0) {
      setErrorMsg("At least one image is required for the blog cover!");
      return;
    }

    setIsSubmitting(true);
    setErrorMsg("");
    setSuccessMsg("");

    const token = localStorage.getItem("portalToken") || localStorage.getItem("adminToken");
    
    const formData = new FormData();
    formData.append("title", title);
    formData.append("content", content);
    formData.append("author", author);
    formData.append("layoutStyle", layoutStyle);
    formData.append("tags", tags);
    
    // Append existing images JSON so backend knows which ones we kept
    formData.append("existingImages", JSON.stringify(existingImages));

    // Append new files
    newImages.forEach((img) => {
      formData.append("images", img);
    });

    try {
      if (editId) {
        await axios.put(`${window.API_URL}/blogs/${editId}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
        setSuccessMsg("Blog updated successfully!");
      } else {
        await axios.post(window.API_URL + "/blogs", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
        setSuccessMsg("Blog published successfully!");
      }
      
      fetchBlogs();
      resetForm();
      // Scroll back up to see success message or listings
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      setErrorMsg(err.response?.data?.message || "An error occurred while saving the blog.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setTitle("");
    setAuthor("Admin");
    setTags("");
    setLayoutStyle("carousel");
    setContent("");
    setNewImages([]);
    setExistingImages([]);
    setEditId(null);
    const fileInput = document.getElementById("blog-images-input");
    if (fileInput) fileInput.value = "";
  };

  const handleEdit = (blog) => {
    setEditId(blog._id);
    setTitle(blog.title);
    setAuthor(blog.author || "Admin");
    setTags(Array.isArray(blog.tags) ? blog.tags.join(", ") : "");
    setLayoutStyle(blog.layoutStyle || "carousel");
    setContent(blog.content);
    setExistingImages(blog.images || []);
    setNewImages([]);
    setErrorMsg("");
    setSuccessMsg("");
    // Scroll to form
    const formElement = document.getElementById("blog-form");
    if (formElement) formElement.scrollIntoView({ behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this blog post? This action cannot be undone.")) return;
    const token = localStorage.getItem("portalToken") || localStorage.getItem("adminToken");
    try {
      await axios.delete(`${window.API_URL}/blogs/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccessMsg("Blog deleted successfully!");
      fetchBlogs();
    } catch (err) {
      console.error(err);
      setErrorMsg("Error deleting blog post.");
    }
  };

  return (
    <div className="max-w-6xl mx-auto pb-12">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Manage Blogs</h1>
          <p className="text-gray-500 mt-1">Publish news, insights, and key announcements to your website.</p>
        </div>
        {editId && (
          <button
            onClick={resetForm}
            className="flex items-center px-4 py-2 bg-gray-200 text-gray-700 hover:bg-gray-300 font-semibold rounded-lg transition"
          >
            Create New Blog instead
          </button>
        )}
      </div>

      {successMsg && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg mb-6 shadow-sm flex items-center justify-between">
          <span className="text-green-800 font-medium">{successMsg}</span>
          <button onClick={() => setSuccessMsg("")} className="text-green-600 hover:text-green-800">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {errorMsg && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg mb-6 shadow-sm flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
          <span className="text-red-800 font-medium">{errorMsg}</span>
        </div>
      )}

      {/* Editor Form */}
      <div id="blog-form" className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-gray-100 mb-10 transition-all">
        <h2 className="text-xl font-bold text-[#f06600] mb-6 flex items-center gap-2">
          {editId ? <Edit2 className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          {editId ? "Edit Blog Post" : "Publish New Blog Post"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Blog Title *</label>
              <input
                type="text"
                placeholder="Enter a catchy blog title..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="w-full border border-gray-200 px-4 py-2.5 rounded-xl outline-none focus:border-[#f06600] transition"
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Author</label>
              <input
                type="text"
                placeholder="Author Name"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                className="w-full border border-gray-200 px-4 py-2.5 rounded-xl outline-none focus:border-[#f06600] transition"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Tags / Categories (Comma separated)</label>
              <input
                type="text"
                placeholder="e.g. Design, React, Startup, Agency"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                className="w-full border border-gray-200 px-4 py-2.5 rounded-xl outline-none focus:border-[#f06600] transition"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Display Layout Style *</label>
              <select
                value={layoutStyle}
                onChange={(e) => setLayoutStyle(e.target.value)}
                className="w-full border border-gray-200 px-4 py-2.5 rounded-xl outline-none focus:border-[#f06600] bg-white transition"
              >
                <option value="carousel">Header Image Slider (Carousel)</option>
                <option value="grid">Premium Interactive Image Grid</option>
                <option value="gallery">Article text first, Grid Gallery at bottom</option>
              </select>
            </div>
          </div>

          {/* Rich Text Editor */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Blog Content *</label>
            <div className="quill-container">
              <ReactQuill
                theme="snow"
                value={content}
                onChange={setContent}
                modules={modules}
                formats={formats}
                placeholder="Write your blog content here... Use headings, bullet points, and styles to structure your thoughts."
                className="h-80 mb-12 rounded-xl"
              />
            </div>
          </div>

          {/* Multiple Image Uploads */}
          <div className="border border-dashed border-gray-200 rounded-2xl p-6 bg-gray-50/50">
            <label className="block text-sm font-bold text-gray-800 mb-2">Blog Images (Upload 1 to 4 images) *</label>
            <p className="text-xs text-gray-500 mb-4">First image acts as the blog thumbnail / main banner on the listing page.</p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              {/* Existing Images (loaded when editing) */}
              {existingImages.map((img, index) => (
                <div key={`exist-${index}`} className="relative group aspect-square bg-gray-100 rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                  <img
                    src={`${window.API_URL}/uploads/${img}`}
                    alt="existing preview"
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  />
                  <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm p-1.5 rounded-full shadow hover:bg-white text-red-500 cursor-pointer transition" onClick={() => removeExistingImage(index)}>
                    <X className="w-4 h-4" />
                  </div>
                  <span className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/60 backdrop-blur-sm text-[10px] text-white rounded font-medium">Saved {index + 1}</span>
                </div>
              ))}

              {/* Newly Selected Images Previews */}
              {newImages.map((img, index) => {
                const previewUrl = URL.createObjectURL(img);
                return (
                  <div key={`new-${index}`} className="relative group aspect-square bg-gray-100 rounded-xl overflow-hidden border border-[#f06600]/30 shadow-sm">
                    <img
                      src={previewUrl}
                      alt="new preview"
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    />
                    <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm p-1.5 rounded-full shadow hover:bg-white text-red-500 cursor-pointer transition" onClick={() => removeNewImage(index)}>
                      <X className="w-4 h-4" />
                    </div>
                    <span className="absolute bottom-2 left-2 px-2 py-0.5 bg-[#f06600] text-[10px] text-white rounded font-medium">New {index + 1}</span>
                  </div>
                );
              })}

              {/* Upload Trigger Button */}
              {existingImages.length + newImages.length < 4 && (
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 hover:border-[#f06600] hover:bg-[#f06600]/5 bg-white cursor-pointer rounded-xl transition aspect-square">
                  <ImageIcon className="w-8 h-8 text-gray-400 group-hover:text-[#f06600] mb-2" />
                  <span className="text-xs text-gray-500 font-semibold">Add Photo</span>
                  <input
                    type="file"
                    id="blog-images-input"
                    multiple
                    onChange={handleImageChange}
                    className="hidden"
                    accept="image/*"
                  />
                </label>
              )}
            </div>
          </div>

          <div className="flex gap-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`flex-1 text-white py-3 rounded-xl font-bold text-center shadow-lg transition-all hover:shadow-xl hover:scale-[1.01] ${
                isSubmitting ? "bg-gray-400 cursor-not-allowed" : "bg-[#f06600] hover:bg-[#d05800]"
              }`}
            >
              {isSubmitting
                ? editId
                  ? "Saving Changes..."
                  : "Publishing Article..."
                : editId
                ? "Update Blog Post"
                : "Publish Blog Post"}
            </button>
            
            {editId && (
              <button
                type="button"
                onClick={resetForm}
                className="px-6 border border-gray-300 text-gray-700 py-3 rounded-xl font-bold hover:bg-gray-50 transition"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Blogs List */}
      <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-gray-100">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Published Articles</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {blogs.map((blog) => (
            <div
              key={blog._id}
              className="border border-gray-100 rounded-2xl overflow-hidden flex flex-col hover:shadow-lg hover:border-gray-200 transition-all duration-300"
            >
              <div className="relative h-48 bg-gray-100 shrink-0">
                {blog.images && blog.images.length > 0 ? (
                  <img
                    src={`${window.API_URL}/uploads/${blog.images[0]}`}
                    alt={blog.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <ImageIcon className="w-12 h-12" />
                  </div>
                )}
                <span className="absolute top-3 right-3 px-2.5 py-1 bg-black/60 backdrop-blur-md text-white text-[11px] font-semibold rounded-full uppercase tracking-wider">
                  {blog.layoutStyle}
                </span>
              </div>
              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center text-xs text-gray-400 gap-2 mb-2">
                  <span>By {blog.author}</span>
                  <span>•</span>
                  <span>{new Date(blog.createdAt).toLocaleDateString()}</span>
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2 hover:text-[#f06600] transition">
                  {blog.title}
                </h3>
                <div
                  className="text-gray-500 text-sm mb-4 line-clamp-3"
                  dangerouslySetInnerHTML={{
                    __html: blog.content.replace(/<[^>]*>/g, ""),
                  }}
                />
                <div className="flex gap-2.5 mt-auto pt-4 border-t border-gray-50">
                  <button
                    onClick={() => handleEdit(blog)}
                    className="flex-1 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 font-bold text-xs rounded-xl flex items-center justify-center gap-1.5 transition"
                  >
                    <Edit2 className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(blog._id)}
                    className="flex-1 py-2 bg-red-50 text-red-600 hover:bg-red-100 font-bold text-xs rounded-xl flex items-center justify-center gap-1.5 transition"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                  <a
                    href={`/blog/${blog.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 bg-gray-50 text-gray-500 hover:bg-gray-100 rounded-xl transition"
                    title="View live article"
                  >
                    <Eye className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          ))}
          {blogs.length === 0 && (
            <div className="col-span-full py-16 text-center text-gray-500 flex flex-col items-center">
              <ImageIcon className="w-16 h-16 text-gray-300 mb-3" />
              <p className="font-semibold text-lg">No articles found</p>
              <p className="text-sm mt-1">Fill out the form above to publish your first blog post.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageBlogs;
